import Users from "@/pages/Users/Users";
import React from "react";
import { Navigate, useParams } from "react-router-dom";
import type { RouteConfig } from "@/types/routestypes";
import Permissions from "@/pages/Permissions/Permissions";
import CustomLibrary from "@/pages/CustomLibrary/CustomLibrary";
import Question from "@/pages/CustomLibrary/Question/Question";
import Challanges from "@/pages/CustomLibrary/Challanges/Challenges";
import FileUpload from "@/pages/CustomLibrary/File/FileUpload";
import EmailTemplate from "@/pages/CustomLibrary/EmailTemplate/EmailTemplate";
import FreeForm from "@/pages/CustomLibrary/FreeForm/FreeForm";
import Assessment from "@/pages/Assessment/Assessment";
import RolesAndPermissions from "@/pages/UserPermission/UserPermission";

const SignUp = React.lazy(() => import("@/pages/SignUp/SignUp"));
const Login = React.lazy(() => import("@/pages/Login/Login"));
const ForgotPassword = React.lazy(
  () => import("@/pages/ForgotPassword/ForgotPassword")
);
const ForgotPasswordOtp = React.lazy(
  () => import("@/pages/OtpVerification/ForgotPasswordOtp/ForgotPasswordOtp")
);
const CreatePassword = React.lazy(
  () => import("@/pages/CreatePassword/CreatePassword")
);
const AcceptInvite = React.lazy(() => import("@/pages/AcceptInvite/AcceptInvite"));

const Dashboard = React.lazy(() => import("@/pages/Dashboard/Dashboard"));
const Roles = React.lazy(() => import("@/pages/Roles/Roles"));
const UserManagement = React.lazy(
  () => import("@/pages/UserPermission/UserRoles/UserManagement")
);
const LandingPage = React.lazy(() => import("@/pages/LandingPage/LandingPage"));
const InterviewKit = React.lazy(
  () => import("@/pages/InterviewKit/InterviewKit")
);
const UserProfile = React.lazy(() => import("@/pages/UserProfile/UserProfile"));
const OrganizationSettings = React.lazy(
  () => import("@/pages/UserPermission/OrganizationSettings/OrganizationSettings")
);
const TakeAssessment = React.lazy(() => import("@/pages/TakeAssessment/TakeAssessment"));
const CandidateReport = React.lazy(() => import("@/pages/CandidateReport/CandidateReport"));

const AssessmentInviteRedirect: React.FC = () => {
  const { token } = useParams();
  return <Navigate to={`/take-assessment/${token}`} replace />;
};



const routes: {
  routes: {
    LANDING_PAGE: { path: string; element: React.ReactElement };
  };
  publicRoutes: Record<string, { path: string; element: React.ReactElement }>;
  privateRoutes: Record<string, RouteConfig>;
} = {
  routes: {
    LANDING_PAGE: {
      path: "/",
      element: <LandingPage />,
    },
  },
  publicRoutes: {
    LOGIN: {
      path: "/login",
      element: <Login />,
    },
    ACCEPT_INVITE: {
      path: "/accept-invite",
      element: <AcceptInvite />,
    },
    CREATE_PASSWORD: {
      path: "/create-password",
      element: <CreatePassword />,
    },
    SIGN_UP: {
      path: "/sign-up",
      element: <SignUp />,
    },
    FORGOT_PASSWORD: {
      path: "/forgot-password",
      element: <ForgotPassword />,
    },
    // OTP_VERIFICATION: {
    //   path: "/otp-verification",
    //   element: <OtpVerification />,
    // },
    FORGOT_PASSWORD_OTP: {
      path: "/forgot-password-verification",
      element: <ForgotPasswordOtp heading="Forget Password!" />,
    },
    EMAIL_VERIFICATION: {
      path: "/email-verification",
      element: <ForgotPasswordOtp heading="Verify your email!" />,
    },
    ASSESSMENT_INVITE_REDIRECT: {
      path: "/assessment/invitation/begin/:token",
      element: <AssessmentInviteRedirect />,
    },
    TAKE_ASSESSMENT: {
      path: "/take-assessment/:token",
      element: <TakeAssessment />,
    },
    ASSESSMENT_PREVIEW: {
      path: "/assessment-preview/:id",
      element: <TakeAssessment isPreview={true} />,
    },
  },
  privateRoutes: {
    DASHBOARD: {
      path: "/dashboard",
      element: <Dashboard />,
      pageName: "Dashboard",
      sidebar: {
        show: true,
        // icon: DashboardIcon,
      },
      permissionName: "get user list",
    },
    ASSESSMENT: {
      path: "/assessment",
      element: <Assessment />,
      pageName: "Assessment",
      sidebar: {
        show: true,
      },
      permissionName: "get user list",
    },
    CANDIDATE_REPORT: {
      path: "/report/:assessmentId/:candidateId",
      element: <CandidateReport />,
      pageName: "Candidate Report",
    },
    CUSTOM_LIBRARY: {
      path: "/custom-library",
      pageName: "Custom Library",
      element: <CustomLibrary />,
      sidebar: {
        show: true,
      },
      children: {
        // BRANDING: {
        //   path: "branding",
        //   element: <div>Branding Page</div>,
        //   pageName: "Branding",
        // },
        EMAILS: {
          path: "emails",
          element: <EmailTemplate />,
          pageName: "Emails",
          sidebar: {
            show: true,
          },
        },
        // MESSAGES: {
        //   path: "messages",
        //   element: <div>Messages Page</div>,
        //   pageName: "Messages",
        // },
        FILES: {
          path: "files",
          element: <FileUpload />,
          pageName: "Files",
          sidebar: {
            show: true,
          },
        },
        CHALLENGES: {
          path: "challenges",
          element: <Challanges />,
          pageName: "Challenges",
          sidebar: {
            show: true,
          },
        },
        // PROJECTS: {
        //   path: "projects",
        //   element: <div>Projects Page</div>,
        //   pageName: "Projects",
        // },
        // INTERVIEWS: {
        //   path: "interviews",
        //   element: <div>Interviews Page</div>,
        //   pageName: "Interviews",
        // },
        QUESTIONS: {
          path: "questions",
          element: <Question />,
          pageName: "Questions",
          sidebar: {
            show: true,
          },
        },
        FREE_FORM: {
          path: "free-form",
          element: <FreeForm />,
          pageName: "Free-form",
          sidebar: {
            show: true,
          },
        },
      },
    },
    // INTERVIEW_KIT: {
    //   path: "/interview-kit",
    //   element: <InterviewKit />,
    //   pageName: "Interview Kit",
    //   sidebar: {
    //     show: false,
    //     // icon: InterviewKitIcon,
    //   },
    // },
    USERS: {
      path: "/users",
      element: <Users />,
      pageName: "Users",
      sidebar: {
        show: false,
        //icon: UserIcon,
      },
      permissionName: "get user list",
    },
    ROLES_AND_PERMISSIONS: {
      path: "/roles-permissions",
      pageName: "Setting",
      element: <RolesAndPermissions />,
      sidebar: {
        show: true,
      },
      children: {
        USER_ROLES: {
          path: "user-roles",
          element: <UserManagement />,
          pageName: "User Management",
          sidebar: {
            show: true,
          },
        },
        PERSONAL_INFO: {
          path: "personal-info",
          element: <UserProfile />,
          pageName: "Personal Info",
          sidebar: {
            show: true,
          },
        },
        ORGANIZATION_SETTINGS: {
          path: "organization-settings",
          element: <OrganizationSettings />,
          pageName: "Organization Settings",
          // sidebar: {
          //   show: true,
          // },
        },
      },
    },
  },
} as const;


export default routes;
